import { type DeviceEntity } from '@solinkcorp/api-types';
import {
  userHasFeature,
  type UserSession,
  type UserWithFeatures,
} from '@solinkcorp/authentication';
import { type CallhomeCamera } from 'utils/CameraUtil';
import { type VideoDiscoverySite } from 'utils/types';
import { type SelectedStream } from './types';
import { type VideoPlayerType } from './VideoUtil';

export const MIN_INTERLINK_VERSION_FOR_WEBRTC = 114;

export interface SupportedPlayerInfo {
  supported: boolean;
  reason?: string;
}

// Subset of UserSession which we actually use.
interface SupportedPlayerUser extends UserWithFeatures {
  turnCredentials?: UserSession['turnCredentials'];
}

// Subset of SelectedStream which we actually use.
interface SupportedPlayerStream {
  source: SelectedStream['source'];
  stream: {
    isMp4Enabled: SelectedStream['stream']['isMp4Enabled'];
  };
}

/** Return true if the user and stream both support playing MP4 video. */
export function isMP4Available(
  user: SupportedPlayerUser,
  selectedStream: SupportedPlayerStream | undefined,
): boolean {
  return isMP4Supported(user, selectedStream).supported;
}

export function isMP4Supported(
  user: SupportedPlayerUser,
  selectedStream: SupportedPlayerStream | undefined,
): SupportedPlayerInfo {
  if (!selectedStream) {
    return { supported: false, reason: 'No stream selected' };
  } else if (selectedStream.source === 'cloud') {
    return { supported: false, reason: 'Cannot play from cloud' };
  } else if (!selectedStream.stream.isMp4Enabled) {
    return { supported: false, reason: 'Stream does not support MP4' };
  } else if (!userHasFeature(user, 'webHlsMp4Live')) {
    return { supported: false, reason: 'User is missing feature: webHlsMp4Live' };
  }
  return { supported: true };
}

export function getSupportedPlayerTypes(params: {
  user: SupportedPlayerUser;
  location: VideoDiscoverySite;
  locationIsLocal: boolean;
  selectedStream: SupportedPlayerStream;
  playingLive: boolean;
  quick?: boolean;
  numberOfPlayingCameras: number;
  isSafari?: boolean;
  device: DeviceEntity | undefined;
  camera: CallhomeCamera;
}): { [key in VideoPlayerType]: SupportedPlayerInfo } {
  return {
    HLS: hlsSupported(),
    ConnectHLS: connectHLSSupported(params.selectedStream),
    WebRTC: webRTCSupported(params),
  };
}

function hlsSupported(): SupportedPlayerInfo {
  // We can always play HLS.
  return { supported: true };
}

function connectHLSSupported(selectedStream: SupportedPlayerStream): SupportedPlayerInfo {
  if (selectedStream.source === 'cloud') {
    return { supported: false, reason: 'Cannot play from cloud' };
  }
  return { supported: true };
}

export function webRTCSupportedBase(
  user: SupportedPlayerUser,
  device: DeviceEntity | undefined,
  camera: CallhomeCamera,
): SupportedPlayerInfo {
  let reason;
  if (!location) {
    reason = 'Location is missing';
  } else if ((device?.currentSoftwareMajorVersion ?? 0) < MIN_INTERLINK_VERSION_FOR_WEBRTC) {
    reason = `Interlink ${device?.currentSoftwareMajorVersion} doesn't support WEBRTC`;
  } else if (!camera?.turnUrl) {
    reason = 'Location is missing TURN config';
  } else if (!user.turnCredentials) {
    reason = 'User is missing TURN credentials';
  } else if (!device?.availablePlaybackMethods?.includes('WEBRTC')) {
    reason = 'Location does not support WEBRTC';
  } else if (!userHasFeature(user, 'playLiveViaWebRTC')) {
    reason = 'User is missing feature: playLiveViaWebRTC';
  }
  return reason === undefined ? { supported: true } : { supported: false, reason };
}

function webRTCSupported(params: {
  user: SupportedPlayerUser;
  device: DeviceEntity | undefined;
  selectedStream: SupportedPlayerStream;
  playingLive: boolean;
  camera: CallhomeCamera;
}): SupportedPlayerInfo {
  const { user, device, selectedStream, playingLive, camera } = params;

  let reason;
  if (selectedStream.source === 'cloud') {
    reason = 'Cannot play from cloud';
  } else if (!playingLive) {
    reason = 'Can only play live';
  }
  return reason === undefined
    ? webRTCSupportedBase(user, device, camera)
    : { supported: false, reason };
}
